import type { ElementType, HTMLAttributes } from 'react';

/** Props for components that stand in for an arbitrary Webflow element. */
export type WfProps = HTMLAttributes<HTMLElement> & {
  as?: ElementType;
  href?: string;
  target?: string;
  [attr: `data-${string}`]: string | undefined;
};

export function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

export function hasClass(className: string | undefined, c: string): boolean {
  return (className ?? '').split(/\s+/).includes(c);
}

/** Removes a class (e.g. the `w--current` Webflow baked into the published HTML). */
export function withoutClass(className: string | undefined, c: string): string {
  return (className ?? '')
    .split(/\s+/)
    .filter((x) => x && x !== c)
    .join(' ');
}

/** Animates an element's height between 0 and its natural height, like Webflow IX "Size" actions. */
export function animateHeight(
  el: HTMLElement,
  open: boolean,
  duration: number,
  easing = 'ease',
): Animation | undefined {
  const from = el.getBoundingClientRect().height;
  el.style.height = open ? 'auto' : '0px';
  const to = open ? el.getBoundingClientRect().height : 0;
  if (typeof el.animate !== 'function' || from === to) {
    if (open) el.style.height = '';
    return undefined;
  }
  const anim = el.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration, easing });
  anim.onfinish = () => {
    // Leave an open element at its natural height so it can reflow.
    if (open) el.style.height = '';
  };
  return anim;
}
